import { For, Show } from "solid-js";
import { etatTemporel, LIBELLES_ETAT } from "../domaine/portefeuille";
import type { DimensionSimilarite, Similarites, Voisin } from "../domaine/similarite";
import type { DateIso, Partenaire, Portefeuille, Projet } from "../domaine/types";
import { formaterPeriode, formaterScore } from "../format";
import { Etiquettes, ListeBioagresseurs } from "./Etiquettes";

const VOISINS_AFFICHES = 5;

/** Noms courts des dimensions, pour la décomposition du score. */
export const NOMS_DIMENSION: Record<DimensionSimilarite, string> = {
  leviers: "familles de leviers",
  cultures: "cultures",
  bioagresseurs: "bioagresseurs",
};

function grouperPartenaires(partenaires: Partenaire[]): [string, string[]][] {
  const groupes = new Map<string, string[]>();
  for (const p of partenaires) {
    const type = p.typeStructure ?? "autre";
    groupes.set(type, [...(groupes.get(type) ?? []), p.nom]);
  }
  return [...groupes.entries()].sort((a, b) => b[1].length - a[1].length);
}

const capitaliser = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Fiche d'un projet et ses voisins les plus proches, avec la décomposition du
 * score — sans elle, un 0,73 ne se discute pas (POC, étape 5).
 */
export function FicheProjet(props: {
  portefeuille: Portefeuille;
  projet: Projet;
  /** Les autres projets, du plus proche au plus lointain. */
  voisins: Voisin[];
  similarites: Similarites;
  aujourdhui: DateIso;
  surSelection: (ref: string | null) => void;
}) {
  const projet = () => props.projet;
  const voisins = () =>
    props.voisins
      .flatMap((v) => {
        const autre = props.portefeuille.projets[v.autre];
        return autre ? [{ ...v, projet: autre }] : [];
      })
      .slice(0, VOISINS_AFFICHES);
  const etat = () => etatTemporel(projet(), props.aujourdhui);
  const leviersActifs = () =>
    props.portefeuille.leviers.filter((l) => projet().leviers.includes(l.id));

  return (
    <>
      <section class="carte" aria-label={`Fiche de ${projet().acronyme}`}>
        <h2 class="acronyme">{projet().acronyme}</h2>
        <Show when={projet().titre}>
          <p class="titre-projet">{projet().titre}</p>
        </Show>
        <dl class="faits">
          <Show when={projet().porteur}>
            <dt>Porteur</dt>
            <dd>
              {projet().porteur}
              <Show when={projet().typePorteur}> · {projet().typePorteur}</Show>
            </dd>
          </Show>
          <dt>Filière</dt>
          <dd>{projet().filiere ?? "transversal"}</dd>
          <Show when={projet().filieresImpliquees.length}>
            <dt>Filières impliquées</dt>
            <dd>
              <Etiquettes valeurs={projet().filieresImpliquees} />
            </dd>
          </Show>
          <Show when={projet().planAction}>
            <dt>Plan d'action</dt>
            <dd>{projet().planAction}</dd>
          </Show>
          <Show when={projet().bioagresseursCommuns.length || projet().bioagresseurs.length}>
            <dt>Bioagresseurs</dt>
            <dd>
              <Show
                when={projet().bioagresseursCommuns.length}
                fallback={<Etiquettes valeurs={projet().bioagresseurs} />}
              >
                <ListeBioagresseurs elements={projet().bioagresseursCommuns} />
              </Show>
            </dd>
          </Show>
          <Show when={projet().bioagresseursLatins.length}>
            <dt>Noms latins</dt>
            <dd>
              <Etiquettes valeurs={projet().bioagresseursLatins} latin />
            </dd>
          </Show>
          {/* Une date manquante se dit, elle ne s'invente pas (R6). */}
          <dt>Calendrier</dt>
          <dd>
            {formaterPeriode(projet())}{" "}
            <span class="valeur-secondaire">({LIBELLES_ETAT[etat()].toLowerCase()})</span>
          </dd>
          <Show when={projet().cultures.length}>
            <dt>Cultures</dt>
            <dd>
              <Etiquettes valeurs={projet().cultures.map((c) => capitaliser(c.nom))} />
            </dd>
          </Show>
        </dl>
        <Show when={projet().lienFiche}>
          {(lien) => (
            <a class="lien-externe" href={lien()} target="_blank" rel="noopener noreferrer">
              Fiche ecophytoPIC
            </a>
          )}
        </Show>
      </section>

      <Show when={props.portefeuille.dimensions.leviers}>
        <section class="carte" aria-labelledby="fiche-leviers">
          <h2 id="fiche-leviers">Familles de leviers</h2>
          {/* Seules les familles mobilisées : la liste des absentes ne dit rien de plus. */}
          <Show
            when={leviersActifs().length}
            fallback={<p class="explication discret">Aucune famille cochée.</p>}
          >
            <ul class="etiquettes">
              <For each={leviersActifs()}>{(l) => <li title={l.libelleLong}>{l.libelle}</li>}</For>
            </ul>
          </Show>
        </section>
      </Show>

      <Show when={projet().partenaires.length}>
        <section class="carte" aria-labelledby="fiche-partenaires">
          <h2 id="fiche-partenaires">Partenaires</h2>
          {/* Par type de structure : un intitulé, puis une étiquette par partenaire. */}
          <For each={grouperPartenaires(projet().partenaires)}>
            {([type, noms]) => (
              <div class="groupe-partenaires">
                <h3>{type}</h3>
                <Etiquettes valeurs={noms} />
              </div>
            )}
          </For>
        </section>
      </Show>

      <section class="carte" aria-labelledby="fiche-voisins">
        <h2 id="fiche-voisins">Projets les plus proches</h2>
        <Show
          when={voisins().length}
          fallback={<p class="explication">Aucun autre projet à comparer.</p>}
        >
          <For each={voisins()}>
            {(v) => (
              <div class="voisin">
                <div class="voisin-tete">
                  <button
                    type="button"
                    class="lien-projet"
                    onClick={() => props.surSelection(v.projet.ref)}
                  >
                    {v.projet.acronyme}
                  </button>
                  <span class="voisin-score">{formaterScore(v.score)}</span>
                </div>
                {/* Le plan d'action n'entre pas dans le calcul : quand il coïncide, le témoin passe. */}
                <Show when={v.projet.planAction && v.projet.planAction === projet().planAction}>
                  <p class="temoin">même plan d'action (hors calcul)</p>
                </Show>
                {/* La décomposition : sans elle, un 0,73 ne se discute pas. */}
                <p class="decomposition">
                  {props.similarites.dimensionsUtilisees
                    .map((d) => `${NOMS_DIMENSION[d]} ${formaterScore(v.detail[d] ?? 0)}`)
                    .join(" · ")}
                </p>
              </div>
            )}
          </For>
        </Show>
      </section>
    </>
  );
}
